/**
 * Custom Knowledge books (empty or titled) that are not yet on any article.
 *
 * The monolith only exposes /api/knowledge/articles (collectionKeys on rows) and
 * /api/knowledge/library (file index). There is no collections/books API, so One
 * keeps an org + knowledge-base scoped client registry in localStorage. There
 * are no Suite presets; article keys still surface as books in the list.
 * When a collections API ships on Workers, replace this module's storage without
 * changing the UI.
 */

import { DEFAULT_KNOWLEDGE_BASE_ID } from "@/lib/knowledgeBaseRegistry";

export type KnowledgeCustomBook = {
  collectionKey: string;
  title: string;
  /** Optional one-line purpose under the title (same idea as libraries). */
  description?: string;
  /** Client registry id of the parent library, when assigned. */
  libraryId?: string;
  createdAt: string;
};

import { publishKnowledgeRegistryForOrg } from "@/lib/knowledgeRegistryCrossSync";

const STORAGE_PREFIX = "atw-one.knowledge.customBooks";
export const KNOWLEDGE_CUSTOM_BOOKS_CHANGED = "atw-one:knowledge-custom-books-changed";

function storageKey(orgId: string, kbId: string): string {
  return `${STORAGE_PREFIX}.${orgId || "default"}.${kbId || DEFAULT_KNOWLEDGE_BASE_ID}`;
}

/** Legacy org-only key (pre multi-KB). Migrated into the Primary base once. */
function legacyStorageKey(orgId: string): string {
  return `${STORAGE_PREFIX}.${orgId || "default"}`;
}

function isCustomBook(value: unknown): value is KnowledgeCustomBook {
  if (!value || typeof value !== "object") return false;
  const row = value as KnowledgeCustomBook;
  return (
    typeof row.collectionKey === "string" &&
    row.collectionKey.trim().length > 0 &&
    typeof row.title === "string" &&
    row.title.trim().length > 0 &&
    typeof row.createdAt === "string" &&
    (row.description === undefined || typeof row.description === "string")
  );
}

export function suggestBookCollectionKey(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return slug || "book";
}

export function normalizeBookCollectionKey(raw: string): string {
  return raw.trim().replace(/\s+/g, "-").slice(0, 120);
}

function parseBookList(raw: string | null): KnowledgeCustomBook[] {
  if (!raw) return [];
  const parsed = JSON.parse(raw) as unknown;
  if (!Array.isArray(parsed)) return [];
  const byKey = new Map<string, KnowledgeCustomBook>();
  for (const item of parsed) {
    if (!isCustomBook(item)) continue;
    const collectionKey = normalizeBookCollectionKey(item.collectionKey);
    if (!collectionKey) continue;
    const libraryId =
      typeof item.libraryId === "string" && item.libraryId.trim() ? item.libraryId.trim() : undefined;
    const description =
      typeof item.description === "string" && item.description.trim()
        ? item.description.trim()
        : undefined;
    byKey.set(collectionKey, {
      collectionKey,
      title: item.title.trim(),
      ...(description ? { description } : {}),
      libraryId,
      createdAt: item.createdAt,
    });
  }
  return [...byKey.values()].sort((a, b) => a.title.localeCompare(b.title));
}

function migrateLegacyBooks(orgId: string, kbId: string): void {
  if (kbId !== DEFAULT_KNOWLEDGE_BASE_ID) return;
  if (typeof window === "undefined") return;
  const org = orgId || "default";
  const scoped = window.localStorage.getItem(storageKey(org, kbId));
  if (scoped) return;
  const legacy = window.localStorage.getItem(legacyStorageKey(org));
  if (!legacy) return;
  try {
    window.localStorage.setItem(storageKey(org, kbId), legacy);
  } catch {
    /* ignore */
  }
}

export function listCustomBooks(
  orgId: string | null | undefined,
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): KnowledgeCustomBook[] {
  if (typeof window === "undefined") return [];
  const baseId = (kbId || "").trim();
  if (!baseId) return [];
  try {
    migrateLegacyBooks(orgId || "default", baseId);
    return parseBookList(window.localStorage.getItem(storageKey(orgId || "default", baseId)));
  } catch {
    return [];
  }
}

function writeCustomBooks(
  orgId: string | null | undefined,
  kbId: string | null | undefined,
  books: KnowledgeCustomBook[],
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(orgId || "default", kbId || DEFAULT_KNOWLEDGE_BASE_ID),
      JSON.stringify(books),
    );
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_BOOKS_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function upsertCustomBook(
  orgId: string | null | undefined,
  input: { title: string; description?: string; collectionKey?: string; libraryId?: string },
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): { ok: true; book: KnowledgeCustomBook } | { ok: false; error: string } {
  const title = input.title.trim();
  if (!title) return { ok: false, error: "Title is required." };
  if (!(kbId || "").trim()) {
    return { ok: false, error: "Select or create a knowledge base first." };
  }

  const collectionKey = normalizeBookCollectionKey(
    input.collectionKey?.trim() ? input.collectionKey : suggestBookCollectionKey(title),
  );
  if (!collectionKey) return { ok: false, error: "Enter a valid key or title." };
  if (collectionKey.startsWith("kb:")) {
    return { ok: false, error: "Keys starting with kb: are reserved for knowledge base membership." };
  }

  const existing = listCustomBooks(orgId, kbId);
  const previous = existing.find((book) => book.collectionKey === collectionKey);
  const libraryId = input.libraryId?.trim() || previous?.libraryId;
  const description =
    input.description !== undefined
      ? input.description.trim()
      : (previous?.description || "").trim();
  const book: KnowledgeCustomBook = {
    collectionKey,
    title,
    ...(description ? { description } : {}),
    libraryId,
    createdAt: previous?.createdAt || new Date().toISOString(),
  };
  const next = [...existing.filter((row) => row.collectionKey !== collectionKey), book].sort((a, b) =>
    a.title.localeCompare(b.title),
  );
  writeCustomBooks(orgId, kbId, next);
  return { ok: true, book };
}

export function getCustomBookTitle(
  orgId: string | null | undefined,
  collectionKey: string,
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): string | null {
  const key = normalizeBookCollectionKey(collectionKey);
  const match = listCustomBooks(orgId, kbId).find((book) => book.collectionKey === key);
  return match?.title || null;
}

/** Drop the client-only custom book list for one knowledge base. */
export function clearCustomBooksForKnowledgeBase(
  orgId: string | null | undefined,
  kbId: string | null | undefined,
): void {
  if (typeof window === "undefined") return;
  const baseId = (kbId || "").trim();
  if (!baseId) return;
  try {
    window.localStorage.removeItem(storageKey(orgId || "default", baseId));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_BOOKS_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore */
  }
}
